// utils/printReceipt.js
import { paymentLabel, tableLabel } from '@/utils/ticketLabels';
import {
  directPrint,
  updateKitchenPrinted,
  printOrderKitchen,
} from "@/api";
import { usePrintJobStore } from "@/stores/usePrintJobStore";

/* Helper: currency symbol from restaurant.currency */
const getCurrencySymbol = (currency = "TND") => {
  const cur = (currency || "").toLowerCase();
  // Europe
  if (cur === "euro" || cur === "eur") return "€";
  if (cur === "gbp" || cur === "pound" || cur === "sterling") return "£";
  if (cur === "chf") return "Fr";
  if (cur === "sek" || cur === "nok" || cur === "dkk") return "kr";
  if (cur === "pln") return "zł";
  // Americas
  if (cur === "usd" || cur === "dollar") return "$";
  if (cur === "cad") return "CA$";
  if (cur === "aud") return "A$";
  if (cur === "brl") return "R$";
  if (cur === "mxn") return "MX$";
  // Asia & Pacific
  if (cur === "jpy" || cur === "yen") return "¥";
  if (cur === "cny" || cur === "rmb" || cur === "yuan") return "¥";
  if (cur === "krw") return "₩";
  if (cur === "inr" || cur === "rupee") return "₹";
  if (cur === "sgd") return "S$";
  if (cur === "hkd") return "HK$";
  // Middle East & Africa
  if (cur === "tnd" || cur === "dinar") return "TND";
  if (cur === "mad") return "MAD";
  if (cur === "dzd") return "DZD";
  if (cur === "egp") return "EGP";
  if (cur === "sar") return "﷼";
  if (cur === "aed") return "د.إ";
  if (cur === "qar") return "﷼";
  if (cur === "kwd") return "KD";
  if (cur === "lyd") return "LYD";
  if (cur === "xof" || cur === "xaf") return "CFA";
  if (cur === "zar") return "R";
  if (cur === "ngn") return "₦";
  if (cur === "mru") return "MRU";
  return (currency || "TND").toUpperCase();
};

/* ──────────────────────────────────────────────────────────
 * MAIN EXPORT: printReceipt
 *  • router        – Next.js router instance (for API helpers)
 *  • order         – full paid-order object (contains order_menus[])
 *  • restaurant    – object with { name, currency } at minimum
 * ──────────────────────────────────────────────────────── */
export const printReceipt = async (router, order, restaurant = {}) => {
  if (!order) return;

  try {
    const currencySymbol = getCurrencySymbol(restaurant.currency || "TND");
    const tableNumber = tableLabel(order.restaurant_table?.name);
    
    // Format items for direct print
    const formattedItems = (order.order_menus || []).map((detail) => {
      const finalItemPrice = parseFloat(detail.order_price || 0);
      
      const questionObjs = (detail.questions || []).map((q) => {
        const selectedAnswers = (q.answers || []).filter((ans) => ans.selected);
        const answersArr = selectedAnswers.map((ans) => {
          const [key] = Object.keys(ans).filter((k) => k !== "selected");
          return `${key} (${ans[key] || 0})`;
        });
        return {
          question: q.question,
          answers: answersArr,
        };
      });

      return {
        name: detail.price?.menu?.name || "Unknown Item",
        quantity: detail.quantity,
        price: parseFloat(finalItemPrice.toFixed(2)),
        notes: detail.notes || "",
        questions: questionObjs,
      };
    });

    // Calculate total
    const total = formattedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);

    const directPrintPayload = {
      table_number: tableNumber,
      items: formattedItems,
      total: parseFloat(total.toFixed(2)),
      payment_method: paymentLabel(order),
      tip_amount: parseFloat(order.tip_amount || 0),
      notes: "",
      restaurant_name: restaurant.name || "",
      currency: currencySymbol,
      discount: order.discount?.toString() || "0",
      location: "receipt",
      principal: true,
    };

    await directPrint(router, directPrintPayload);
  } catch (err) {
    console.error("Receipt print failed:", err);
  }
};

/* ──────────────────────────────────────────────────────────
 * AUTO PRINT LOCATION TICKETS
 *  • router        – Next.js router instance
 *  • order         – order object with order_menus[]
 *  • restaurant    – restaurant object with automatic_print, name, currency
 * ──────────────────────────────────────────────────────── */
// Kitchen-ticket requests already sent from this tab, by order and lines (bounded).
const requestedKitchenTickets = new Set();
const KITCHEN_REQUEST_MEMORY = 200;
const kitchenRequestKey = (order) => {
  const lineIds = (order.order_menus || []).map((line) => line.id).sort((a, b) => a - b);
  return `${order.id}:${lineIds.join(",")}`;
};
const rememberKitchenRequest = (key) => {
  requestedKitchenTickets.add(key);
  if (requestedKitchenTickets.size > KITCHEN_REQUEST_MEMORY) {
    requestedKitchenTickets.delete(requestedKitchenTickets.values().next().value);
  }
};

export const autoPrintLocationTickets = async (router, order, restaurant = {}) => {

  if (!order || !restaurant.automatic_print) {
    return;
  }

  // Backends with the print queue: never build the tickets here.
  //  * server_auto_print: the API already sent them (no open dashboard needed) —
  //    sending ours too would print twice;
  //  * otherwise the API builds them from the order, idempotently (another tab,
  //    a repeated order::new or the API itself never print the same lines twice),
  //    and marks the lines kitchen_printed once a print app confirms.
  let capabilities = null;
  try {
    capabilities = await usePrintJobStore.getState().loadCapabilities(router);
  } catch {
    capabilities = null;
  }
  if (capabilities?.server_auto_print) {
    return;
  }
  if (capabilities?.jobs) {
    // One request per order and set of lines from this tab: the POS that placed the
    // order, its own order::new and a replayed event all land here for the same order.
    const key = kitchenRequestKey(order);
    if (requestedKitchenTickets.has(key)) return;
    rememberKitchenRequest(key);
    try {
      const jobs = await printOrderKitchen(router, order.id);
      jobs.forEach((job) => usePrintJobStore.getState().upsert(job));
    } catch (err) {
      requestedKitchenTickets.delete(key); // a later event may try again
      console.error("❌ Auto-print (print queue) failed:", err);
    }
    return;
  }

  // Older backend: previous behaviour.
  try {
    // For paid orders, skip auto-print
    if (order.status === 3) { // PAID status
      return;
    }

    // Filter only NEW items that haven't been kitchen printed
    // Accept status 0 (PENDING) or status 1 (IN_PROGRESS/CONFIRMED)
    const newItems = (order.order_menus || []).filter(item => 
      (item.status === 0 || item.status === 1) && !item.kitchen_printed
    );
    
    
    // Debug: Log the status and kitchen_printed state of each item
    if (order.order_menus && order.order_menus.length > 0) {
      order.order_menus.forEach((item, index) => {
      });
    }

    if (newItems.length === 0) {
      return;
    }


    // Always use Direct Print API
    await handleDirectPrint(router, order, newItems, restaurant);

    // After successful printing, mark items as kitchen printed
    const itemIds = newItems.map(item => item.id);
    await updateKitchenPrinted(router, order.id, itemIds);

  } catch (err) {
    console.error("❌ Auto-print location tickets failed:", err);
  }
};

/* ──────────────────────────────────────────────────────────
 * Helper: Handle Direct Print using Direct Print API
 * ──────────────────────────────────────────────────────── */
const handleDirectPrint = async (router, order, itemsToPrint, restaurant) => {
  const currencySymbol = getCurrencySymbol(restaurant.currency || "TND");
  
  // Group items by location
  const groupedByLocation = {};
  itemsToPrint.forEach((item) => {
    const location = (item.prepared_at || "unknown").toLowerCase();
    if (!groupedByLocation[location]) {
      groupedByLocation[location] = [];
    }
    groupedByLocation[location].push(item);
  });


  // Print each location group using Direct Print API
  for (const [location, items] of Object.entries(groupedByLocation)) {
    const tableNumber = tableLabel(order.restaurant_table?.name);
    
    // Format items for direct print
    const formattedItems = items.map((detail) => {
      // Use backend-calculated order_price instead of recalculating
      const finalItemPrice = parseFloat(detail.order_price || 0);
      
      const questionObjs = (detail.questions || []).map((q) => {
        const selectedAnswers = (q.answers || []).filter((ans) => ans.selected);
        const answersArr = selectedAnswers.map((ans) => {
          const [key] = Object.keys(ans).filter((k) => k !== "selected");
          return `${key} (${ans[key] || 0})`;
        });
        return {
          question: q.question,
          answers: answersArr,
        };
      });

      return {
        name: detail.price?.menu?.name || "Unknown Item",
        quantity: detail.quantity,
        price: parseFloat(finalItemPrice.toFixed(2)),
        notes: detail.notes || "",
        questions: questionObjs,
      };
    });

    const directPrintPayload = {
      table_number: tableNumber,
      items: formattedItems,
      total: 0, // Not needed for location tickets
      payment_method: "Cash",
      notes: "",
      restaurant_name: restaurant.name || "",
      currency: currencySymbol,
      discount: "0",
      location: location,
      principal: false,
    };

    await directPrint(router, directPrintPayload);
  }

};

export default printReceipt;
